import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, Box, Checkbox, Chip, TextField } from '@mui/material';
import { standardQuoteSearch } from 'services';

export interface ClientOption {
  id: number;
  name: string;
  code?: string;
  buCode?: string;
}

interface ClientMultiSelectProps {
  label: string;
  value: ClientOption[];
  onChange: (clients: ClientOption[]) => void;
}

const formatClientOption = (client: ClientOption | null | undefined) =>
  client ? [client.name, client.code, client.buCode].filter(Boolean).join(' - ') : '';

// Search text and results stay local so typing does not re-render the dashboard charts
export const ClientMultiSelect = memo(({ label, value, onChange }: ClientMultiSelectProps) => {
  const [clientList, setClientList] = useState<ClientOption[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const latestSearchRef = useRef('');

  useEffect(() => {
    const query = searchValue.trim();
    latestSearchRef.current = query;

    // Keep the last results when the text is cleared after a selection, so more clients can be ticked
    if (!query) {
      setIsLoading(false);
      return;
    }

    const timer = window.setTimeout(() => {
      setIsLoading(true);
      standardQuoteSearch(query).then((res: any) => {
        if (latestSearchRef.current === query) {
          setClientList(res?.status === 200 && Array.isArray(res.data) ? res.data : []);
        }
      }).catch(() => {
        if (latestSearchRef.current === query) {
          setClientList([]);
        }
      }).finally(() => {
        if (latestSearchRef.current === query) {
          setIsLoading(false);
        }
      });
    }, 400);

    return () => window.clearTimeout(timer);
  }, [searchValue]);

  useEffect(() => {
    if (value.length === 0) {
      setSearchValue('');
      setClientList([]);
    }
  }, [value.length]);

  const options = useMemo(() => {
    const selectedIds = new Set(value.map((client) => client.id));
    return [...value, ...clientList.filter((client) => !selectedIds.has(client.id))];
  }, [value, clientList]);

  return (
    <Autocomplete<ClientOption, true, false, false>
      multiple
      disableCloseOnSelect
      options={options}
      value={value}
      inputValue={searchValue}
      onInputChange={(event, newValue, reason) => {
        if (reason === 'input') {
          setSearchValue(newValue);
        } else if (reason === 'clear') {
          setSearchValue('');
          setClientList([]);
        }
      }}
      onChange={(event, newValue) => {
        onChange(newValue);
        setSearchValue('');
      }}
      onClose={() => setSearchValue('')}
      loading={isLoading}
      filterOptions={(items) => items}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      getOptionLabel={(option) => formatClientOption(option)}
      // Same tag layout focused or not, so the field height never changes and the Search button does not shift on blur
      renderTags={(tags, getTagProps) => tags.length === 0 ? null : (
        <>
          <Chip
            {...getTagProps({ index: 0 })}
            size="small"
            label={formatClientOption(tags[0])}
            sx={{ maxWidth: 'calc(100% - 70px)' }}
          />
          {tags.length > 1 && (
            <Box component="span" sx={{ mx: 0.5, fontSize: 13, whiteSpace: 'nowrap' }}>+{tags.length - 1}</Box>
          )}
        </>
      )}
      sx={{ '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' } }}
      renderOption={(props, option, { selected }) => (
        <li {...props} key={option.id}>
          <Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0.5 }} />
          {formatClientOption(option)}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length === 0 ? 'Search Client (Name/Code/BU)' : ''}
          size="small"
          fullWidth
        />
      )}
      noOptionsText={searchValue.trim() ? (isLoading ? 'Loading...' : 'No clients found') : 'Type to search clients'}
      size="small"
      fullWidth
    />
  );
});
